import { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RothoSSOSettings, useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { isClientError } from "../../http/errors";
import { useProjectSharingAccessesApi } from "../../http/useProjectSharingAccesses";
import Button from "../ui/Button";
import SubmitBar from "../ui/SubmitBar";
import styles from "./ManageProjectInvite.module.css";

type ManageProjectInviteProps = {
  settings: RothoSSOSettings;
};

const ManageProjectInvite: FC<ManageProjectInviteProps> = ({ settings }: ManageProjectInviteProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const projectSharingAccessesApi = useProjectSharingAccessesApi();

  const { token } = useParams<{ token: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clicked, setClicked] = useState<boolean>(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAcceptCallback = searchParams.get("accepted") === "true";

  const navigate = useNavigate();

  const acceptInvite = () => {
    if (token) {
      setIsSubmitting(true);
      projectSharingAccessesApi
        .acceptProjectSharingAccessInvite(token)
        .then(({ projectId }) => {
          setClicked(true);
          dispatch(
            showSuccessNotification(i18n.translation, i18n.translation.projects.invitationStatus.statusChanges.accept),
          );
          navigate(`/projects/${projectId}`);
        })
        .catch((err) => dispatch(showErrorNotification(i18n, err)))
        .finally(() => setIsSubmitting(false));
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    if (isAcceptCallback && auth.identity && !clicked) {
      acceptInvite();
    }
  }, [auth.identity]);

  const { data: projectData, error: projectDataError } =
    projectSharingAccessesApi.useProjectSharingAccessInviteProjectData(token ?? "");
  const inviteNotFound = isClientError(projectDataError);
  const invitePageTranslation = { ...i18n.translation.common.invitePage, ...i18n.translation.projects.invitePage };
  const authorName =
    [projectData?.author.firstName, projectData?.author.lastName].filter(Boolean).join(" ") ||
    invitePageTranslation.fallbackAuthorName;

  const submit = (accept: boolean) => {
    setIsSubmitting(true);

    if (accept) {
      if (!auth.identity) {
        if (!isAcceptCallback) {
          const acceptUrl = new URL(window.location.href);
          acceptUrl.searchParams.set("accepted", "true");
          acceptUrl.searchParams.set("email_locked", "1");
          projectData?.invitedEmail && acceptUrl.searchParams.set("email", projectData.invitedEmail);
          window.location.href = `${settings.iamUrl}?redirect_uri=${acceptUrl.toString()}`;
        }
        auth.init(true);
        return;
      } else {
        acceptInvite();
        return;
      }
    } else {
      if (token) {
        projectSharingAccessesApi
          .rejectProjectSharingAccessInvite(token)
          .then(() => {
            setClicked(true);
            dispatch(
              showSuccessNotification(
                i18n.translation,
                i18n.translation.projects.invitationStatus.statusChanges.rejected,
              ),
            );
          })
          .catch((err) => dispatch(showErrorNotification(i18n, err)))
          .finally(() => setIsSubmitting(false));
      }
    }
  };

  return (
    <div className={styles.centerContent}>
      <section className={styles.card}>
        <div className={styles.inviteHero}>
          <span className={styles.badge}>{invitePageTranslation.badge}</span>
          <h2 className={styles.projectName}>
            {projectData?.name ??
              (inviteNotFound ? invitePageTranslation.notFoundTitle : invitePageTranslation.loadingProjectDetails)}
          </h2>
          {!inviteNotFound ? (
            <p className={styles.lead}>
              <strong>{authorName}</strong> {invitePageTranslation.collaborationLead}
            </p>
          ) : undefined}
        </div>

        {!inviteNotFound ? (
          <>
            <div className={styles.detailsGrid}>
              <div className={styles.detailCard}>
                <span className={styles.detailLabel}>{invitePageTranslation.invitedBy}</span>
                <span className={styles.detailValue}>{authorName}</span>
              </div>
              <div className={styles.detailCard}>
                <span className={styles.detailLabel}>{invitePageTranslation.invitationSentTo}</span>
                <span className={styles.detailValue}>
                  {projectData?.invitedEmail ?? invitePageTranslation.notAvailable}
                </span>
              </div>
            </div>

            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>{invitePageTranslation.projectName}</span>
                <span className={styles.summaryValue}>{projectData?.name ?? invitePageTranslation.notAvailable}</span>
              </div>
              {projectData?.description ? (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>{invitePageTranslation.description}</span>
                  <p className={styles.description}>{projectData.description}</p>
                </div>
              ) : undefined}
            </div>
          </>
        ) : undefined}

        <div className={styles.notice}>
          {inviteNotFound
            ? invitePageTranslation.inviteNotFound
            : !projectData
              ? ""
              : !projectData.actionRequired
                ? invitePageTranslation.inviteNoLongerAvailable
                : auth.identity
                  ? invitePageTranslation.acceptSignedIn
                  : invitePageTranslation.acceptSignedOut}
        </div>

        {!inviteNotFound ? (
          <div className={styles.actions}>
            <SubmitBar>
              <Button
                buttonProps={{
                  disabled: isSubmitting || clicked || !projectData?.actionRequired,
                  onClick: () => submit(true),
                }}
              >
                {invitePageTranslation.acceptAction}
              </Button>
              <Button
                type="secondary"
                buttonProps={{
                  disabled: isSubmitting || clicked || !projectData?.actionRequired,
                  onClick: () => submit(false),
                }}
              >
                {invitePageTranslation.rejectAction}
              </Button>
            </SubmitBar>
          </div>
        ) : undefined}
      </section>
    </div>
  );
};

export default ManageProjectInvite;
